import { useCallback } from "react"
import { Effect, Dispatch } from "react-use-elmish"

export const mapEffect = <Key extends string, T>(
  key: Key,
  effect: Effect<T>,
): Effect<{ type: Key; action: T }> => {
  return effect.map(e => (dispatch: Dispatch<{ type: Key; action: T }>) => {
    e(action => {
      return dispatch({
        type: key,
        action,
      })
    })
  })
}

function mapDispatch<ChildAction, Key extends string>(
  dispatch: Dispatch<{ type: Key; action: ChildAction }>,
  action: ChildAction,
  type: Key,
): void
function mapDispatch<ChildAction, Key1 extends string, Key2 extends string>(
  dispatch: Dispatch<{
    type: Key1
    action: { type: Key2; action: ChildAction }
  }>,
  action: ChildAction,
  type1: Key1,
  type2: Key2,
): void
function mapDispatch<ChildAction>(
  dispatch: Dispatch<{ type: string; action: ChildAction }>,
  action: ChildAction,
  ...types: string[]
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newAction = types.reduceRight<any>((prev, type) => {
    return {
      type,
      action: prev,
    }
  }, action)
  dispatch(newAction)
}

export function useMappedDispatch<ChildAction, Key extends string>(
  dispatch: Dispatch<{ type: Key; action: ChildAction }>,
  type: Key,
) {
  return useCallback(
    (action: ChildAction) => mapDispatch(dispatch, action, type),
    [dispatch, type],
  )
}

export function mapChildUpdate<
  TParentState,
  TStateKey extends keyof TParentState & string,
  TChildAction,
  TParentAction extends { type: TStateKey; action: TChildAction },
>(
  update: (
    state: TParentState[TStateKey],
    action: TChildAction,
  ) => [TParentState[TStateKey], Effect<TChildAction>],
  stateKey: TStateKey,
  state: TParentState,
  action: TParentAction,
): [TParentState, Effect<{ type: TStateKey; action: TChildAction }>]
export function mapChildUpdate<
  TParentState,
  TStateKey extends keyof TParentState & string,
  TChildAction,
  TParentAction extends { type: TActionKey; action: TChildAction },
  TActionKey extends string,
>(
  update: (
    state: TParentState[TStateKey],
    action: TChildAction,
  ) => [TParentState[TStateKey], Effect<TChildAction>],
  stateKey: TStateKey,
  state: TParentState,
  action: TParentAction,
  actionKey: TActionKey,
): [TParentState, Effect<{ type: TActionKey; action: TChildAction }>]
export function mapChildUpdate<
  TParentState,
  TStateKey extends keyof TParentState & string,
  TChildAction,
  TParentAction extends { type: TActionKey; action: TChildAction },
  TActionKey extends string,
>(
  update: (
    state: TParentState[TStateKey],
    action: TChildAction,
  ) => [TParentState[TStateKey], Effect<TChildAction>],
  stateKey: TStateKey,
  state: TParentState,
  action: TParentAction,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actionKey: any = stateKey,
): [TParentState, Effect<{ type: TActionKey; action: TChildAction }>] {
  const oldChildState = state[stateKey]

  const [newChildState, newEffect] = update(oldChildState, action.action)

  const newState =
    newChildState === oldChildState
      ? state
      : {
          ...state,
          [stateKey]: newChildState,
        }

  return [newState, mapEffect(actionKey, newEffect)]
}
